import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';

import type { IResourceAnnouncementItem, IResourceClientMember } from '@/api/types';
import type { ITreeFolder } from '@/components/common';
import type { IAnnouncementEditForm } from './types';
import type { IAgentPosition } from '@/store/types';
import type { IFileProxy } from '@/components/common/form/types';
import type { IDateFormatter } from '@/types/date';

import { Button, DatePicker, Input } from '@/components/common';
import { announcementEditSchema } from './modalSchemas';
import { formData, mapFolderTreeToBasicClientFolder } from '@/helpers';
import {
  Checkbox,
  CheckList,
  ClientMemberSelect,
  FileInput,
  FolderSelect,
  Select,
  WYSIWYGInput,
} from '@/components/common/form';
import { AnnouncementType } from '@/store/enums';
import { ATTACHMENT_SIZE_LIMIT, TIME_ISO_FORMAT } from '@/config/variables';
import { useAppSelector, useTimezone } from '@/hooks';
import { ResourceMetadataSection } from '@/components/common/resources/modals';
import { CalendarBlue, ChevronDropdown, TimeBlue } from '@/assets/icons';

import { useDispatch } from 'react-redux';
import { FilterControl } from '@/components/common/filter-control';
import { ALL_VALUE, buildNewState } from '@/helpers/multi-select-checkbox';
import { getCountriesList } from '@/store/slices/utilsSlice';
import { getAgentPositionByClientIdAndCountryIds } from '@/store/slices/pub/agentPositionSlice';

interface IFilterList {
  value: number;
  label: string;
  checked: boolean;
}

const defaultValues = (parentFolderSignature: string, userId: number): IAnnouncementEditForm => ({
  folder: parentFolderSignature,
  title: '',
  body: '',
  startDate: '',
  startTime: '',
  endDate: '',
  endTime: '',
  stayOnTop: false,
  countries: [],
  recipients: [],
  attachments: [],
  type: AnnouncementType.regular,
  notify: false,
  everyone: false,
  createdBy: userId,
});

const mapResourceToFormValues = (
  resource: IResourceAnnouncementItem,
  parentFolderSignature: string,
  availableRecipients: string[],
  formatDate: IDateFormatter,
): IAnnouncementEditForm => {
  const recipients = resource.everyone ? availableRecipients : resource.recipients?.map(String) ?? [];
  const files =
    resource.attachments?.map<IFileProxy>(attachment => ({
      file: null,
      path: attachment,
      name: attachment.substring(attachment.lastIndexOf('/') + 1),
    })) ?? [];

  return {
    folder: parentFolderSignature,
    title: resource.title,
    body: resource.body || '',
    startDate: new Date(resource.dateStart),
    startTime: formatDate(resource.dateStart, TIME_ISO_FORMAT),
    endDate: resource.dateEnd === undefined || resource.dateEnd === null ? '' : new Date(resource.dateEnd),
    endTime:
      resource.dateEnd === undefined || resource.dateEnd === null ? '' : formatDate(resource.dateEnd, TIME_ISO_FORMAT),
    stayOnTop: resource.stayOnTop,
    recipients: recipients,
    countries: [],
    attachments: files,
    type: resource.type,
    notify: resource.notify,
    everyone: resource.everyone,
    createdBy: resource.createdBy,
  };
};

interface IAnnouncementFormProps {
  onSubmit: (data: IAnnouncementEditForm) => void;
  onCancel: () => void;
  existingResource?: IResourceAnnouncementItem;
  foldersList: ITreeFolder[];
  clientId: number;
  parentFolderId: number;
  availablePositions: IAgentPosition[];
  clientMemberList: IResourceClientMember[];
  userId: number;
}

const AnnouncementForm = ({
  onSubmit,
  onCancel,
  existingResource,
  foldersList,
  clientId,
  parentFolderId,
  availablePositions,
  clientMemberList,
  userId,
}: IAnnouncementFormProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { formatDateTime } = useTimezone();
  const folderOptions = useMemo(
    () => mapFolderTreeToBasicClientFolder(foldersList, 'announcement', clientId),
    [foldersList, clientId],
  );
  const parentFolderSignature = useMemo(
    () => JSON.stringify({ id: parentFolderId, clientId }),
    [clientId, parentFolderId],
  );
  const { countriesList } = useAppSelector(state => state.utils);
  const [countriesFilterList, setCountriesFilterList] = useState<IFilterList[]>([]);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, touchedFields },
  } = useForm<IAnnouncementEditForm>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(announcementEditSchema),
    defaultValues: existingResource
      ? mapResourceToFormValues(
          existingResource,
          parentFolderSignature,
          availablePositions.map(position => position.agentPositionId.toString()),
          formatDateTime,
        )
      : defaultValues(parentFolderSignature, userId),
  });

  useEffect(() => {
    dispatch(getCountriesList());
  }, [dispatch]);

  useEffect(() => {
    const selectedCountries: IFilterList[] = countriesList.map(c => ({
      value: c.id,
      label: c.countryname,
      checked: existingResource?.countries?.find(e => e === c.id) ? true : false,
    }));
    const isSelectAll = selectedCountries.filter(c => c.checked).length === selectedCountries.length;
    const allCountries: IFilterList = { value: ALL_VALUE, label: 'Select All', checked: isSelectAll };
    selectedCountries.unshift(allCountries);
    setCountriesFilterList(selectedCountries);
  }, [countriesList, existingResource]);

  useEffect(() => {
    setValue('folder', parentFolderSignature);
  }, [parentFolderSignature, setValue]);

  useEffect(() => {
    if (existingResource) {
      reset(
        mapResourceToFormValues(
          existingResource,
          parentFolderSignature,
          availablePositions.map(position => position.agentPositionId.toString()),
          formatDateTime,
        ),
      );
    }
  }, [availablePositions, existingResource, formatDateTime, parentFolderSignature, reset]);

  const formDataOptions = {
    register,
    touchedFields,
    errors,
  };

  const recipientOptions = useMemo(
    () =>
      availablePositions.map(position => ({
        label: position.agentPositionName,
        value: position.agentPositionId.toString(),
        name: 'recipients',
      })),
    [availablePositions],
  );

  const isNotifyChecked = watch('notify');

  useEffect(() => {
    const watchSubscription = watch((value, { name }) => {
      if (name === 'recipients' && value.recipients?.length !== availablePositions.length) {
        setValue('everyone', false);
      }
    });

    return () => watchSubscription.unsubscribe();
  }, [availablePositions.length, setValue, watch]);

  useEffect(() => {
    dispatch(
      getAgentPositionByClientIdAndCountryIds({
        clientId,
        countryIds: countriesFilterList.filter(c => c.checked).map(c => c.value),
      }),
    );
  }, [dispatch, clientId]);

  useEffect(() => {
    if (countriesFilterList) {
      const countryIds = countriesFilterList
        .filter(c => c.value > 0)
        .filter(c => c.checked)
        .map(c => c.value);
      setValue('countries', countryIds);
    }
  }, [countriesFilterList, onSubmit]);

  const onCountryChange = (e: IFilterList[]) => {
    const newState = buildNewState(e, countriesFilterList);
    setCountriesFilterList(newState);
    const countryIds = newState
      .filter(c => c.value > 0)
      .filter(c => c.checked)
      .map(c => c.value);
    setValue('countries', countryIds);
  };

  const getDateLimit = (param: 'startDate' | 'endDate') => (watch(param) ? new Date(watch(param)) : undefined);

  return (
    <Announcement>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col xs={12} xl={6}>
            <Select
              name="type"
              label={t`resources.modals.type`}
              options={[
                { value: AnnouncementType.regular, label: t`resources.modals.announcementType.regular` },
                { value: AnnouncementType.alert, label: t`resources.modals.announcementType.alert` },
              ]}
              {...formData('type', formDataOptions)}
            />
          </Col>
          <Col xs={12} xl={6}>
            <FolderSelect
              name="folder"
              folders={folderOptions}
              label={t`resources.modals.folder`}
              {...formData('folder', formDataOptions)}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12}>
            <label>{t`shippingAddress.country`}</label>
            <FilterControl
              label=""
              controlShouldRenderValue={true}
              onSelectOptions={onCountryChange}
              options={countriesFilterList}
              disabled={false}
              isSingleSelect={false}
              disableDefaultSelectedStyle={true}
              showLabels={true}
              selectedValue={countriesFilterList
                .filter(country => country.checked)
                .map(c => ({ value: c.value, label: c.label }))}
            />
          </Col>
        </Row>
        <Input
          name="title"
          type="text"
          label={t`resources.modals.name`}
          autoFocus
          required
          {...formData('title', formDataOptions)}
        />
        <Controller
          name="body"
          control={control}
          render={({ field, fieldState }) => (
            <WYSIWYGInput {...field} {...fieldState} label={t`resources.modals.announcementBody`} />
          )}
        />
        <LineSpacer />
        <Controller
          name="attachments"
          control={control}
          render={({ field, fieldState }) => (
            <FileInput
              label={t`resources.modals.attachments`}
              name="attachments"
              buttonLabel={t`resources.modals.addAttachment`}
              value={field.value}
              error={fieldState.error}
              setValue={files => setValue('attachments', files)}
              maxFiles={2}
              maxSize={ATTACHMENT_SIZE_LIMIT}
            />
          )}
        />
        <Row>
          <Col xs={12} xl={6}>
            <InputLabel htmlFor="startDate">{t`resources.modals.publicationDate`}</InputLabel>
            <DateTimeContainer>
              <DatePicker
                placeholderText="yyyy/mm/dd"
                name="startDate"
                className="form-date-input"
                {...formData('startDate', formDataOptions)}
                maxDate={getDateLimit('endDate')}
                control={control}
              />
              <Input name="startTime" type="time" required={true} {...formData('startTime', formDataOptions)} />
            </DateTimeContainer>
          </Col>
          <Col xs={12} xl={6}>
            <InputLabel htmlFor="endDate">{t`resources.modals.expirationDate`}</InputLabel>
            <DateTimeContainer>
              <DatePicker
                placeholderText="yyyy/mm/dd"
                name="endDate"
                className="form-date-input"
                {...formData('endDate', formDataOptions)}
                minDate={getDateLimit('startDate')}
                control={control}
              />
              <Input name="endTime" type="time" required={true} {...formData('endTime', formDataOptions)} />
            </DateTimeContainer>
          </Col>
        </Row>
        <LineSpacer />
        <StyledCheckbox
          name="stayOnTop"
          label={t`resources.modals.stayOnTop`}
          {...formData('stayOnTop', formDataOptions)}
        />
        <StyledCheckbox name="notify" label={t`resources.modals.notify`} {...formData('notify', formDataOptions)} />
        {isNotifyChecked && (
          <Controller
            name="everyone"
            control={control}
            render={({ field }) => (
              <CheckList
                name="recipients"
                label={t`resources.modals.recipients`}
                checkboxes={recipientOptions}
                selectAll={isChecked => {
                  setValue('everyone', isChecked);
                  setValue('recipients', isChecked ? recipientOptions.map(option => option.value) : []);
                }}
                selectAllValue={field.value}
                selectAllLabel={t`resources.modals.everyone`}
                {...formData('recipients', formDataOptions)}
              />
            )}
          />
        )}
        <ClientMemberSelect
          name="createdBy"
          label={t`resources.modals.createdBy`}
          clientMemberList={clientMemberList}
          metaData={existingResource}
          {...formData('createdBy', formDataOptions)}
        />
        <ResourceMetadataSection item={existingResource} />
        <FormControl>
          <StyledButton color="blueOutline" className="me-3" onClick={onCancel}>
            {t`common.btnCancel`}
          </StyledButton>
          <StyledButton color="blue" type="submit">
            {existingResource ? t`common.btnSave` : t`common.btnCreate`}
          </StyledButton>
        </FormControl>
      </form>
    </Announcement>
  );
};

const StyledButton = styled(Button)`
  width: 190px;
`;

const InputLabel = styled.label.attrs({
  className: 'mb-1',
})`
  font-size: 16px;
  color: #374151;
  font-weight: 400;
  line-height: 20px;
  padding-bottom: 5px;
`;

const DateTimeContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  & > :first-child,
  & > :last-child {
    width: 47%;
  }

  .form-date-input {
    background-image: white;
    background-image: url(${CalendarBlue});
    background-repeat: no-repeat;
    background-position: right 9px center;
    background-color: white;
  }

  .form-date-input:focus {
    border: none !important;
  }

  input {
    height: 48px;
  }
`;

const Announcement = styled.div`
  .styled-select {
    background-image: url(${ChevronDropdown});
    background-size: 14px 14px, 14px 14px, 0px 1.5em;
    bottom: 0;
  }

  svg {
    width: 25px;
    height: 25px;
  }

  [class*='-indicatorContainer'] svg {
    color: #008acd;
  }

  .react-datepicker-wrapper svg {
    color: #008acd;
    width: 25px;
    height: 25px;
  }

  .drop-area-button {
    width: 153px;
    height: 32px;
    border-radius: 8px;
    margin-bottom: 8px;
    font-size: 13px;
    font-style: normal;
    font-weight: 600;
    line-height: 20px;
  }

  .startDate,
  .endDate {
    background-image: url(${CalendarBlue});
    background-repeat: no-repeat;
    background-position: right 9px center;

    &[type='date']::-webkit-calendar-picker-indicator {
      opacity: 0;
    }
  }

  .startTime,
  .endTime {
    background-image: url(${TimeBlue});
    background-repeat: no-repeat;
    background-position: right 9px center;

    &[type='time']::-webkit-calendar-picker-indicator {
      opacity: 0;
    }
  }

  .checkbox-items:first-child {
    border-bottom: 1px solid #dadee1;
  }
`;

const FormControl = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

const LineSpacer = styled.div`
  border-top: 1px solid #dee2e6;
  margin-bottom: 24px;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: 18px;
`;

export { AnnouncementForm };
